import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import {
  useCreateEscalation,
  useEscalationContext,
} from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { ChatAddToolApproveResponseFunction } from "ai";
import type { ReactNode } from "react";
import { memo, useState } from "react";
import * as styles from "./ask-redo-escalate-to-human-card.module.css";
import { buildAskRedoChatHistory } from "./build-ask-redo-chat-history";
import { useChatMessagesRef } from "./chat-messages-context";

export interface AskRedoEscalateToHumanCardProps {
  /** The dynamic-tool part's approval id, passed through from the
   *  `approval-requested` state. */
  approvalId: string;
  /** Tool input — { reason, issueCategory, product, customerContext }.
   *  Used to populate the createEscalation payload. */
  input: unknown;
  addToolApprovalResponse: ChatAddToolApproveResponseFunction;
  /** True once the tool has resolved (state === "output-available"
   *  or "approval-responded"). The card stays mounted in those states
   *  but transitions to a non-interactive "connected" placeholder so
   *  the parent doesn't have to re-render the part stream. */
  resolved: boolean;
}

interface ToolInput {
  reason?: string;
  issueCategory?: string;
  product?: string;
  customerContext?: string | null;
}

function readToolInput(input: unknown): ToolInput {
  if (!isRecord(input)) {
    return {};
  }
  return {
    reason: typeof input.reason === "string" ? input.reason : undefined,
    issueCategory:
      typeof input.issueCategory === "string" ? input.issueCategory : undefined,
    product: typeof input.product === "string" ? input.product : undefined,
    customerContext:
      typeof input.customerContext === "string" ? input.customerContext : null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export const AskRedoEscalateToHumanCard = memo(
  function AskRedoEscalateToHumanCard({
    approvalId,
    input,
    addToolApprovalResponse,
    resolved,
  }: AskRedoEscalateToHumanCardProps) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const escalationContext = useEscalationContext();
    const chatMessagesRef = useChatMessagesRef();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createEscalation = useCreateEscalation({
      merchantRpcClient,
      buildChatHistory: () => buildAskRedoChatHistory(chatMessagesRef.current),
    });

    // Check if the user already has an open escalation on a different
    // thread — drives the "you already have an open conversation"
    // branch. Mirrors the help-chat flow.
    const activeEscalationLoad = useLoad(
      async (signal) => {
        const result = await merchantRpcClient.checkActiveEscalation(
          {},
          { signal },
        );
        return result.activeEscalation;
      },
      [merchantRpcClient],
    );
    const activeEscalation = activeEscalationLoad.value;
    const triageThreadId = escalationContext.triageThreadId;
    const hasExistingEscalationOnDifferentThread =
      activeEscalation != null &&
      activeEscalation.triageThreadId !== triageThreadId;

    const denyApproval = useHandler(() => {
      addToolApprovalResponse({ id: approvalId, approved: false });
    });

    const approveAndEscalate = useHandler(async () => {
      if (!triageThreadId) {
        setError("Unable to escalate: conversation thread not found");
        return;
      }
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      setError(null);

      const toolInput = readToolInput(input);
      try {
        // Agent often re-calls escalate-to-human after a successful
        // handoff — each click would create a duplicate Intercom
        // conversation. Skip createEscalation only when the active
        // escalation is BOUND to the current thread; otherwise we'd
        // approve without creating a new escalation, silently
        // dropping the handoff.
        //
        // Compare against `escalation.triageThreadId` (the bound id
        // captured at escalation creation) and NOT the floating
        // `escalationContext.triageThreadId` (which equals
        // `triageThreadId` above by aliasing — a tautology). The
        // floating-vs-bound distinction is exactly what the "Start a
        // new one" path through the "you already have an open
        // conversation" branch depends on: there the active
        // escalation lives on a DIFFERENT thread, so its bound id
        // won't match the current one and createEscalation runs.
        const alreadyEscalated =
          escalationContext.escalation.escalationId !== null &&
          escalationContext.escalation.status !== "closed" &&
          escalationContext.escalation.triageThreadId === triageThreadId;
        if (!alreadyEscalated) {
          await createEscalation({
            triageThreadId,
            escalationReason: toolInput.reason,
            issueCategory: toolInput.issueCategory,
            product: toolInput.product,
          });
        }
        addToolApprovalResponse({ id: approvalId, approved: true });
      } catch (err) {
        // Surface to DevTools + Datadog RUM. The card also shows the
        // message inline, but a console error gives us breadcrumbs
        // tied to whatever else the merchant was doing.
        console.error(
          "[ask-redo] escalate-to-human createEscalation failed",
          { triageThreadId, issueCategory: toolInput.issueCategory },
          err,
        );
        const message =
          err instanceof Error ? err.message : "Failed to connect to support";
        setError(message);
        setIsLoading(false);
      }
    });

    const goToExisting = useHandler(() => {
      if (activeEscalation && escalationContext.navigateToThread) {
        escalationContext.navigateToThread(activeEscalation.triageThreadId);
        // Deny this approval — the user is heading to the other thread.
        addToolApprovalResponse({ id: approvalId, approved: false });
      }
    });

    if (resolved) {
      return null;
    }

    if (activeEscalationLoad.pending) {
      return <AskRedoEscalateToHumanPendingCard />;
    }

    if (hasExistingEscalationOnDifferentThread) {
      return (
        <EscalationCard
          description="Would you like to continue your existing conversation?"
          error={error}
          title="You already have an open conversation with the team"
        >
          <Flex gap="sm" minW="none" w="full">
            <RedoButton
              disabled={isLoading}
              flexBasis="0"
              flexGrow={1}
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={approveAndEscalate}
              pending={isLoading}
              size="sm"
              text={isLoading ? "Connecting…" : "Start a new one"}
            />
            <RedoButton
              disabled={isLoading || !escalationContext.navigateToThread}
              flexBasis="0"
              flexGrow={1}
              hierarchy="primary"
              IconTrailing={ArrowRightIcon}
              onClick={goToExisting}
              size="sm"
              text="Go to existing"
            />
          </Flex>
        </EscalationCard>
      );
    }

    return (
      <EscalationCard
        description="A team member will join the conversation to help."
        error={error}
        title="Would you like to connect with our team?"
      >
        <Flex gap="sm" minW="none" w="full">
          <RedoButton
            disabled={isLoading}
            flexBasis="0"
            flexGrow={1}
            hierarchy="secondary"
            onClick={denyApproval}
            size="sm"
            text="Not yet"
          />
          <RedoButton
            disabled={isLoading}
            flexBasis="0"
            flexGrow={1}
            hierarchy="primary"
            IconLeading={MessageChatCircleIcon}
            onClick={approveAndEscalate}
            pending={isLoading}
            size="sm"
            text={isLoading ? "Connecting…" : "Yes, connect me"}
          />
        </Flex>
      </EscalationCard>
    );
  },
);

export const AskRedoEscalateToHumanPendingCard = memo(
  function AskRedoEscalateToHumanPendingCard() {
    return (
      <EscalationCard
        description="Checking whether you already have an open conversation."
        error={null}
        title="Preparing support handoff"
      >
        <Flex align="center" gap="xs" wrap="wrap">
          <RedoSpinner size="xs" style="track" />
          <Text fontSize="sm" textColor="secondary">
            Just a moment…
          </Text>
        </Flex>
      </EscalationCard>
    );
  },
);

const EscalationCard = memo(function EscalationCard({
  title,
  description,
  error,
  children,
}: {
  title: string;
  description: string;
  error: string | null;
  children: ReactNode;
}) {
  return (
    <Flex
      align="center"
      alignSelf="flex-start"
      className={styles.card}
      dir="column"
      gap="md"
      maxW="md"
      p="lg"
      w="full"
    >
      <Header description={description} error={error} title={title} />
      {children}
    </Flex>
  );
});

const Header = memo(function Header({
  title,
  description,
  error,
}: {
  title: string;
  description: string;
  error: string | null;
}) {
  return (
    <Flex align="center" dir="column" gap="xxs" minW="none" w="full">
      <Text
        fontSize="sm"
        fontWeight="semibold"
        textAlign="center"
        textColor="primary"
      >
        {title}
      </Text>
      <Text fontSize="sm" textAlign="center" textColor="secondary">
        {description}
      </Text>
      {error && (
        <Text fontSize="xs" mt="xxs" textAlign="center" textColor="error">
          {error}
        </Text>
      )}
    </Flex>
  );
});
